<template>
  <div class="home detail-actions-page">
    <div class="px-8 py-4">
      <!-- Header -->
      <div class="detail-page-header mb-4">
        <button type="button" class="btn btn-link text-decoration-none detail-page-back" @click="$router.go(-1)" style="padding: 10px 0">
          <AppIcon name="bi-arrow-left" class="bi bi-arrow-left me-2" />{{$t('back')}}
        </button>
        <div class="detail-page-title-row d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-3">
          <div class="d-flex align-items-center gap-2">
            <h3 class="detail-page-title mb-0">{{ planning.title }}</h3>
            <span :class="'badge bg-' + getStatusColor(planning.status)">{{ $t(planning.status) }}</span>
            <span :class="'badge bg-' + getPriorityColor(planning.priority)">{{ $t(planning.priority) }}</span>
          </div>
          <div class="detail-page-actions">
            <button @click="editPlanning" style="padding: 5px;margin: 0;font-size: 12px !important;" class="btn btn-primary btn-xs detail-page-action--secondary">
              <AppIcon name="bi-pencil" class="bi bi-pencil" style="font-size:12px" /> {{$t('edit')}}
            </button>
            <button @click="deletePlanning" style="padding: 5px;margin: 0;font-size: 12px !important;" class="btn btn-primary btn-xs detail-page-action--danger">
              <AppIcon name="bi-trash" class="bi bi-trash" style="font-size:12px" /> {{$t('delete')}}
            </button>
          </div>
        </div>
      </div>

      <!-- Loading -->
      <div v-if="loading" class="text-center" style="padding:2rem">
        <div class="spinner-border" role="status">
          <span class="visually-hidden">{{$t('loading')}}</span>
        </div>
      </div>

      <!-- Content -->
      <div v-else-if="planning && planning.id">
        <!-- Tabs -->
        <ul class="nav nav-tabs detail-page-tabs mb-4">
          <li class="nav-item">
            <a class="nav-link" :class="{active: step === 'overview'}" @click="step = 'overview'">{{$t('overview')}}</a>
          </li>
          <li class="nav-item">
            <a class="nav-link" :class="{active: step === 'activity'}" @click="step = 'activity'">
              {{$t('activity')}}
              <span v-if="planning.activities && planning.activities.length" class="badge bg-secondary ms-1">{{planning.activities.length}}</span>
            </a>
          </li>
          <li class="nav-item">
            <a class="nav-link" :class="{active: step === 'attachments'}" @click="step = 'attachments'">
              {{$t('attachments')}}
              <span v-if="planning.attachments && planning.attachments.length" class="badge bg-secondary ms-1">{{planning.attachments.length}}</span>
            </a>
          </li>
        </ul>

        <!-- Overview Tab -->
        <div v-if="step === 'overview'">
          <div class="row">
            <div class="col-md-8">
              <!-- Planning Info -->
              <div class="card mb-4">
                <div class="card-header bg-white">
                  <h6 class="mb-0"><AppIcon name="bi-calendar-range" class="bi bi-calendar-range me-2" />{{$t('planning')}} {{$t('information')}}</h6>
                </div>
                <div class="card-body">
                  <div class="row">
                    <div class="col-md-6 mb-3">
                      <small class="text-muted d-block">{{$t('title')}}</small>
                      <strong>{{planning.title}}</strong>
                    </div>
                    <div class="col-md-6 mb-3">
                      <small class="text-muted d-block">{{$t('status')}}</small>
                      <span :class="'badge bg-' + getStatusColor(planning.status)">{{ $t(planning.status) }}</span>
                    </div>
                    <div class="col-md-6 mb-3">
                      <small class="text-muted d-block">{{$t('date')}}</small>
                      <strong>{{ formatDate(planning.date) }}</strong>
                    </div>
                    <div class="col-md-6 mb-3">
                      <small class="text-muted d-block">{{$t('time')}}</small>
                      <strong>{{ planning.start_time || '--:--' }} - {{ planning.end_time || '--:--' }}</strong>
                    </div>
                    <div class="col-md-6 mb-3">
                      <small class="text-muted d-block">{{$t('priority')}}</small>
                      <span :class="'badge bg-' + getPriorityColor(planning.priority)">{{ $t(planning.priority) }}</span>
                    </div>
                    <div class="col-md-6 mb-3">
                      <small class="text-muted d-block">{{$t('assigned_to')}}</small>
                      <strong>{{ planning.assignedUser ? planning.assignedUser.name : '-' }}</strong>
                    </div>
                    <div class="col-md-6 mb-3">
                      <small class="text-muted d-block">{{$t('client')}}</small>
                      <strong>{{ planning.client ? planning.client.name : '-' }}</strong>
                    </div>
                    <div class="col-md-6 mb-3">
                      <small class="text-muted d-block">{{$t('created_at')}}</small>
                      <strong>{{ formatDate(planning.created_at) }}</strong>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Location -->
              <div class="card mb-4" v-if="planning.address">
                <div class="card-header bg-white">
                  <h6 class="mb-0"><AppIcon name="bi-geo-alt" class="bi bi-geo-alt me-2" />{{$t('location')}}</h6>
                </div>
                <div class="card-body">
                  <div class="row">
                    <div class="col-md-12 mb-3" v-if="planning.address">
                      <small class="text-muted d-block">{{$t('address')}}</small>
                      <strong>{{ planning.address }}</strong>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Description & Instructions -->
              <div class="card mb-4" v-if="planning.description || planning.instructions">
                <div class="card-header bg-white">
                  <h6 class="mb-0"><AppIcon name="bi-file-text" class="bi bi-file-text me-2" />{{$t('details')}}</h6>
                </div>
                <div class="card-body">
                  <div v-if="planning.description" class="mb-3">
                    <small class="text-muted d-block">{{$t('description')}}</small>
                    <p class="mb-0" style="white-space: pre-wrap;">{{ planning.description }}</p>
                  </div>
                  <div v-if="planning.instructions">
                    <small class="text-muted d-block">{{$t('instructions')}}</small>
                    <p class="mb-0" style="white-space: pre-wrap;">{{ planning.instructions }}</p>
                  </div>
                </div>
              </div>

              <!-- Feedback -->
              <div class="card mb-4" v-if="planning.feedback">
                <div class="card-header bg-white">
                  <h6 class="mb-0"><AppIcon name="bi-chat-quote" class="bi bi-chat-quote me-2" />{{$t('field_feedback')}}</h6>
                </div>
                <div class="card-body">
                  <p class="mb-0" style="white-space: pre-wrap;">{{ planning.feedback }}</p>

                  <div v-if="planning.attachments && planning.attachments.some(a => a.filetype && a.filetype.includes('image'))" class="mt-3">
                    <label class="small text-muted fw-bold d-block mb-2">{{$t('photos')}}</label>
                    <div class="d-flex flex-wrap gap-2">
                      <div v-for="img in planning.attachments.filter(a => a.filetype && a.filetype.includes('image'))" :key="img.id" class="position-relative">
                        <img :src="img.filepath" class="rounded border" style="width: 80px; height: 80px; object-fit: cover; cursor: pointer;" @click="previewAttachment(img.filepath)">
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Right Column: Summary -->
            <div class="col-md-4">
              <div class="card mb-4">
                <div class="card-header bg-white">
                  <h6 class="mb-0"><AppIcon name="bi-bar-chart" class="bi bi-bar-chart me-2" />{{$t('summary')}}</h6>
                </div>
                <div class="card-body">
                  <div class="mb-3">
                    <small class="text-muted d-block">{{$t('activity')}}</small>
                    <strong>{{ planning.activities ? planning.activities.length : 0 }}</strong>
                  </div>
                  <div class="mb-3">
                    <small class="text-muted d-block">{{$t('attachments')}}</small>
                    <strong>{{ planning.attachments ? planning.attachments.length : 0 }}</strong>
                  </div>
                  <div class="mb-3" v-if="planning.completed_date">
                    <small class="text-muted d-block">{{$t('completed_at')}}</small>
                    <strong>{{ formatDate(planning.completed_date) }}</strong>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Activity Tab -->
        <div v-if="step === 'activity'">
          <div class="card mb-4">
            <div class="card-header bg-white">
              <h6 class="mb-0"><AppIcon name="bi-clock-history" class="bi bi-clock-history me-2" />{{$t('activity')}}</h6>
            </div>
            <div class="card-body">
              <div v-if="planning.activities && planning.activities.length" class="timeline">
                <div v-for="act in sortedActivities" :key="act.id" class="timeline-item pb-3 mb-3 border-bottom">
                  <div class="d-flex gap-3">
                    <div class="flex-shrink-0">
                      <div class="rounded-circle bg-light d-flex align-items-center justify-content-center" style="width: 36px; height: 36px;">
                        <AppIcon :name="getActivityIcon(act.activity_type)" :class="getActivityIcon(act.activity_type)" style="font-size: 14px;" />
                      </div>
                    </div>
                    <div class="flex-grow-1">
                      <div class="d-flex justify-content-between">
                        <strong class="small">{{ act.user ? act.user.name : '-' }}</strong>
                        <small class="text-muted">{{ formatDateTime(act.created_at) }}</small>
                      </div>
                      <p class="mb-0 text-muted small">{{ formatActivityDescription(act) }}</p>
                      <div v-if="act.old_value && act.new_value" class="mt-1">
                        <span class="badge bg-secondary me-1">{{ $t(act.old_value) }}</span>
                        <AppIcon name="bi-arrow-left" :class="$i18n.locale === 'ar' ? 'bi bi-arrow-left small text-muted' : 'bi bi-arrow-right small text-muted'" />
                        <span class="badge bg-primary ms-1">{{ $t(act.new_value) }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div v-else class="text-center text-muted py-4">
                <AppIcon name="bi-clock-history" class="bi bi-clock-history" style="font-size: 2rem;" />
                <p class="mt-2">{{$t('no_activity_yet')}}</p>
              </div>
            </div>
          </div>
        </div>

        <!-- Attachments Tab -->
        <div v-if="step === 'attachments'">
          <Attachment entity_type="planning" :entity_id="planning.id" />
        </div>
      </div>
    </div>

    <!-- Edit Modal -->
    <AddEditPlanningModal v-if="showEditModal" :planning="planning" @close="showEditModal = false" @refresh="getPlanning" />
  </div>
</template>

<script>
import helper from '../../helper'
import AddEditPlanningModal from '../../components/planning/AddEditPlanningModal.vue'
import Attachment from '../../components/Attachment.vue'

export default {
  name: 'PlanningDetails',
  components: { AddEditPlanningModal, Attachment },
  data() {
    return {
      planning: {},
      loading: false,
      step: 'overview',
      showEditModal: false
    }
  },
  computed: {
    sortedActivities() {
      if (!this.planning.activities) return []
      return [...this.planning.activities].sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
    }
  },
  mounted() {
    if(helper.checkAccess('view_plannings') == -1) return this.$router.push({path : '/'})
    if(helper.checkAccess('view_plannings') == -2) return this.$router.push({path : '/subscribe'})

    this.getPlanning()
  },
  methods: {
    formatDate(d) { return helper.formatDate(d) },
    formatDateTime(d) { return helper.formatDateTime(d) },
    async getPlanning() {
      try {
        this.loading = true
        const res = await helper.customGet(`${process.env.VUE_APP_API}/plannings/${this.$route.params.id}`)
        this.planning = res.data
      } catch (e) {
        alert(this.$t('error_fetching_planning'))
      } finally {
        this.loading = false
      }
    },
    editPlanning() {
      this.showEditModal = true
    },
    async deletePlanning() {
      const result = await this.$swal.fire({
        title: `${this.$t('are_you_sure')}?`,
        text: this.$t('you_cant_revert'),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: this.$t('yes_delete_it')
      })
      if (result.isConfirmed) {
        await helper.customDelete(`${process.env.VUE_APP_API}/plannings/${this.planning.id}`)
        this.$router.push('/plannings')
      }
    },

    getStatusColor(status) {
      const colors = { planned: 'primary', in_progress: 'info', completed: 'success', failed: 'danger', postponed: 'warning' }
      return colors[status] || 'secondary'
    },
    getPriorityColor(priority) {
      const colors = { low: 'info', medium: 'primary', high: 'warning', urgent: 'danger' }
      return colors[priority] || 'primary'
    },
    formatActivityDescription(activity) {
      if (activity.activity_type === 'status_change' && activity.new_value) {
        return activity.old_value
          ? this.$t('status_changed_from_to', {
            from: this.$t(activity.old_value),
            to: this.$t(activity.new_value)
          })
          : this.$t('status_updated_to', { status: this.$t(activity.new_value) })
      }
      if (activity.activity_type === 'update' && activity.description === 'Planning created') {
        return this.$t('planning_created')
      }
      return activity.description
    },
    previewAttachment(url) {
      window.open(url, '_blank')
    },
    getActivityIcon(type) {
      const icons = {
        status_change: 'bi bi-arrow-repeat text-primary',
        comment: 'bi bi-chat-dots text-success',
        update: 'bi bi-pencil text-info'
      }
      return icons[type] || 'bi bi-circle-fill text-muted'
    }
  }
}
</script>
